import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Lock, Mail, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { signIn, signOut, getAdminProfile } from "../lib/supabase";
import { useAdminAuth } from "../contexts/AdminAuthContext";

export const AdminLogin = () => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const location = useLocation();
	const { isAuthenticated } = useAdminAuth();

	// Toujours calculer une destination SÛRE (jamais /admin/login)
	const rawFrom = location.state?.from?.pathname;
	const from =
		rawFrom && !rawFrom.startsWith("/admin/login") ? rawFrom : "/admin";

	console.log(
		"[AdminLogin] Render — isAuthenticated:",
		isAuthenticated,
		"from:",
		from,
	);

	// Redirection dès que isAuthenticated devient true
	useEffect(() => {
		console.log("[AdminLogin] useEffect — isAuthenticated:", isAuthenticated);
		if (isAuthenticated) {
			console.log("[AdminLogin] → Redirection vers", from);
			window.location.replace(from);
		}
	}, [isAuthenticated, from]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!email || !password) {
			toast.error("Veuillez remplir tous les champs");
			return;
		}
		setIsLoading(true);
		try {
			console.log("[Login] Tentative...");
			const { data, error } = await signIn(email, password);
			if (error) throw error;
			console.log("[Login] Auth OK:", data.user.id);

			const { data: profile, error: profileError } = await getAdminProfile();
			console.log("[Login] Profil:", profile);

			if (profileError) {
				await signOut();
				toast.error("Erreur de vérification");
				setIsLoading(false);
				return;
			}

			if (!profile) {
				await signOut();
				toast.error("Compte non autorisé");
				setIsLoading(false);
				return;
			}

			toast.success(`Bienvenue ${profile.first_name} 👋`);
			// La redirection est faite par le useEffect quand isAuthenticated passe à true
		} catch (err) {
			console.error("[Login] Erreur:", err);
			toast.error(err.message || "Identifiants incorrects");
			setIsLoading(false);
		}
	};

	return (
		<>
			<Helmet>
				<title>Administration | Weblax</title>
				<meta name="robots" content="noindex, nofollow" />
			</Helmet>

			<div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-primary/5 to-background p-4">
				<div className="w-full max-w-md">
					<div className="text-center mb-8">
						<div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-2xl mb-4">
							<ShieldCheck className="w-7 h-7 text-primary" />
						</div>
						<h1 className="weblax-heading-2 mb-2">Administration Weblax</h1>
						<p className="weblax-body text-sm">Accès réservé à l'équipe</p>
					</div>

					<Card className="weblax-card">
						<CardContent className="p-6 md:p-8">
							<form onSubmit={handleSubmit} className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="email">Email</Label>
									<div className="relative">
										<Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
										<Input
											id="email"
											type="email"
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											placeholder="votre@email.com"
											className="weblax-input pl-10"
											autoComplete="email"
											required
										/>
									</div>
								</div>
								<div className="space-y-2">
									<Label htmlFor="password">Mot de passe</Label>
									<div className="relative">
										<Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
										<Input
											id="password"
											type="password"
											value={password}
											onChange={(e) => setPassword(e.target.value)}
											placeholder="••••••••"
											className="weblax-input pl-10"
											autoComplete="current-password"
											required
										/>
									</div>
								</div>
								<Button
									type="submit"
									disabled={isLoading}
									className="weblax-btn-primary w-full"
								>
									{isLoading ? (
										<>
											<Loader2 className="w-4 h-4 mr-2 animate-spin" />
											Connexion...
										</>
									) : (
										"Se connecter"
									)}
								</Button>
							</form>
						</CardContent>
					</Card>

					<p className="text-center text-xs text-muted-foreground mt-6">
						Problème de connexion ? Contactez un super admin.
					</p>
				</div>
			</div>
		</>
	);
};

export default AdminLogin;
